import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {
  AsyncValidatorFn,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { DialogResult } from "src/app/common/dialog/dialog.component";
import { LogClass, Logger } from "src/app/logger";
import { CirculationMetadataService } from "src/app/services";
import { UserFacade } from "src/app/user/services/user.facade";
import { SubSink } from "subsink";
import { Circulation, IMetaData, IUser } from "../../../interfaces";

@LogClass()
@Component({
    selector: "circ-form-basic",
    templateUrl: "./basic.component.html",
    styleUrls: ["./basic.component.scss"],
    standalone: false
})
export class CirculationFormBasicComponent implements OnInit, OnDestroy {
  @Input()
  @Output()
  public circulation: Circulation;

  @Output()
  public abort = new EventEmitter<void>();

  @Output()
  public save = new EventEmitter<{
    circulation: Circulation;
    navigateToOverview: boolean;
  }>();

  @Output()
  public error = new EventEmitter<string>();

  private currentUser: IUser;
  private subSink = new SubSink();
  public editableMetadata: IMetaData[];
  public ownerDialogOpened = false;
  public formGroup = new UntypedFormGroup({
    title: new UntypedFormControl("", [Validators.required]),
    demand: new UntypedFormControl("", [Validators.required]),
    owner: new UntypedFormControl("", [Validators.required]),
    recipients: new UntypedFormControl([])
  });

  private logger: Logger;

  constructor(
    private metadataService: CirculationMetadataService,
    private userFacade: UserFacade
  ) {
    this.logger.debug("ctor");
  }
  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public clickAbort(): void {
    this.abort.emit();
  }

  public ngOnInit(): void {
    if (!this.circulation) {
      this.error.emit("missing circulation!");
      return;
    }

    this.logger.info("injected circ:" + this.circulation.id);
    this.editableMetadata = this.metadataService.getEditableMetadata(
      this.circulation
    );
    this.subSink.sink = this.userFacade
      .getCurrentUser()
      .subscribe((x) => (this.currentUser = x));
    this.setupForm();
  }

  private setupForm() {
    this.formGroup.setValue({
      title: this.circulation.props.title,

      demand: this.circulation.props.payload.demand,
      owner: this.circulation.props.owner,
      recipients: this.circulation.props.payload.additionalMailRecipients || []
    });

    // programmatically set form controls for each meta data
    const metaGroup: UntypedFormGroup = new UntypedFormGroup({});
    this.editableMetadata.forEach((element) => {
      const validators: (ValidatorFn | AsyncValidatorFn)[] = [];

      if (element.settings.isVisibleInUi && element.settings.isRequired) {
        validators.push(Validators.required);
      }

      metaGroup.addControl(
        element.key + element.valueType,
        new UntypedFormControl(element.value, validators)
      );
    });
    this.formGroup.addControl("meta", metaGroup);
  }

  public onEditDialogClosed(result: DialogResult): void {
    if (result !== DialogResult.Yes) {
      return;
    }

    const circulation = this.prepareSave();
    this.save.emit({
      circulation: circulation,
      navigateToOverview:
        circulation.props.owner.filter((x) => x.login == this.currentUser.login)
          ?.length == 0
    });
  }
  public clickSave(): void {
    this.formGroup.markAllAsTouched();
    if (!this.formGroup.valid) {
      return;
    }
    if (
      !areEqual(
        this.formGroup.value.owner.map((x) => x.login),
        this.circulation.props.owner.map((x) => x.login)
      )
    ) {
      this.ownerDialogOpened = true;
      return;
    }
    // create copy of meta data with updated form values
    const circulation = this.prepareSave();
    this.save.emit({ circulation: circulation, navigateToOverview: false });
  }

  private prepareSave() {
    const updatedMetaData: IMetaData[] = [];
    (this.circulation.props.payload?.data || []).forEach((element) => {
      let update = { ...element };
      if (
        typeof this.formGroup.value.meta !== "undefined" &&
        typeof this.formGroup.value.meta[element.key + element.valueType] !==
          "undefined"
      ) {
        update = {
          ...element,
          value: this.formGroup.value.meta[element.key + element.valueType]
        };
      }
      updatedMetaData.push(update);
    });
    //Immutable --> Prevent ERROR TypeError: Cannot assign to read only property 'title' of object '[object Object]'
    const circulation = new Circulation({
      ...this.circulation.props,
      title: this.formGroup.value.title,
      owner: this.formGroup.value.owner,
      payload: {
        ...this.circulation.props.payload,
        demand: this.formGroup.value.demand,
        additionalMailRecipients: this.formGroup.value.recipients || [],
        data: updatedMetaData
      }
    });
    return circulation;
  }
}

function areEqual(a: string[], b: string[]) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  a = a.sort();
  b = b.sort();

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
